import { AxiosError, AxiosResponse } from "axios";
import { useCallback, useMemo } from "react";

import { client } from "shared/core/clients/brightAuthClient";
import { setAuthHeaders } from "shared/core/hooks/headers";
import { useSSOProviderStatusRequest } from "shared/request/hooks/useSSOProviderStatusRequest/useSSOProviderStatusRequest";
import { AuthSsoState } from "core/state/authSsoState";
import { useAuthVersioning } from "core/hooks/useAuthVersioning";

export type ProviderStatus = {
  id: string | null | undefined;
  email: string | null | undefined;
  disabled: boolean;
  isLoading: boolean;
  error: AxiosError | undefined;
};

export type ProvidersToggles = "Google" | "Microsoft" | "Apple" | "Facebook";

export type useSSOHook = {
  unlink: (provider: string) => Promise<AxiosResponse>;
  setup: (provider: string) => void;
  status: { [K in ProvidersToggles]: ProviderStatus };
  signedUpWithSso: boolean;
  hasSetupSso: boolean;
};

export const useSSO = (): useSSOHook => {
  const googleHook = useSSOProviderStatusRequest({ provider: "Google" });
  const microsoftHook = useSSOProviderStatusRequest({ provider: "Microsoft" });
  const appleHook = useSSOProviderStatusRequest({ provider: "Apple" });
  const facebookHook = useSSOProviderStatusRequest({ provider: "Facebook" });
  const { handleSsoSetup } = useAuthVersioning();

  const status: { [K in ProvidersToggles]: ProviderStatus } = useMemo(
    () => ({
      Google: {
        id: googleHook?.data?.id,
        email: googleHook?.data?.email,
        disabled: !!googleHook?.data?.usedForSignup,
        isLoading: googleHook?.isLoading,
        error: googleHook?.error,
      },
      Microsoft: {
        id: microsoftHook?.data?.id,
        email: microsoftHook?.data?.email,
        disabled: !!microsoftHook?.data?.usedForSignup,
        isLoading: microsoftHook?.isLoading,
        error: microsoftHook?.error,
      },
      Apple: {
        id: appleHook?.data?.id,
        email: appleHook?.data?.email,
        disabled: !!appleHook?.data?.usedForSignup,
        isLoading: appleHook?.isLoading,
        error: appleHook?.error,
      },
      Facebook: {
        id: facebookHook?.data?.id,
        email: facebookHook?.data?.email,
        disabled: !!facebookHook?.data?.usedForSignup,
        isLoading: facebookHook?.isLoading,
        error: facebookHook?.error,
      },
    }),
    [
      googleHook?.data?.id,
      googleHook?.data?.email,
      googleHook?.data?.usedForSignup,
      googleHook?.isLoading,
      googleHook?.error,
      microsoftHook?.data?.id,
      microsoftHook?.data?.email,
      microsoftHook?.data?.usedForSignup,
      microsoftHook?.isLoading,
      microsoftHook?.error,
      appleHook?.data?.id,
      appleHook?.data?.email,
      appleHook?.data?.usedForSignup,
      appleHook?.isLoading,
      appleHook?.error,
      facebookHook?.data?.id,
      facebookHook?.data?.email,
      facebookHook?.data?.usedForSignup,
      facebookHook?.isLoading,
      facebookHook?.error,
    ]
  );

  const signedUpWithSso = useMemo(() => Object.values(status).some((value) => value.disabled), [status]);

  const hasSetupSso = useMemo(() => Object.values(status).some((value) => !!value.id && !value.disabled && !value.error), [status]);

  const mutateMap = useMemo(
    (): Record<ProvidersToggles, () => void> => ({
      Google: googleHook.mutate,
      Microsoft: microsoftHook.mutate,
      Apple: appleHook.mutate,
      Facebook: facebookHook.mutate,
    }),
    [appleHook?.mutate, facebookHook?.mutate, googleHook?.mutate, microsoftHook?.mutate]
  );

  const setup = useCallback(
    (provider: string) => {
      AuthSsoState.set(provider);
      handleSsoSetup(provider);
    },
    [handleSsoSetup]
  );

  const unlink = useCallback(
    async (provider: string) => {
      const axiosConfig = {
        headers: {},
      };
      setAuthHeaders(true, { ...axiosConfig });

      return client.post(`/sso/${provider}/unlink`, undefined, axiosConfig).then((res) => {
        mutateMap[provider as ProvidersToggles]();
        return res;
      });
    },
    [mutateMap]
  );
  return { unlink, setup, status, signedUpWithSso, hasSetupSso };
};
